<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useMapGetter } from 'dashboard/composables/store';
import { useInternalChatStore } from 'dashboard/stores/internalChat';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentPresenceDot from './AgentPresenceDot.vue';
import MessageComposer from './MessageComposer.vue';

const { t } = useI18n();
const store = useInternalChatStore();
const { activeThreadId, messagesByThread } = storeToRefs(store);

const currentUserId = useMapGetter('getCurrentUserID');
const myTeams = useMapGetter('teams/getMyTeams');

const scrollContainer = ref(null);
const loadingOlder = ref(false);
const error = ref(false);

const activeThread = computed(() =>
  store.threads.find(thr => thr.id === activeThreadId.value)
);

const otherParticipant = computed(() => {
  const thread = activeThread.value;
  if (!thread || thread.thread_type === 'team') return null;
  return (thread.participants || []).find(p => p.id !== currentUserId.value);
});

const headerTitle = computed(() => {
  const thread = activeThread.value;
  if (!thread) return '';
  if (thread.thread_type === 'team') {
    const team = (myTeams.value || []).find(tm => tm.id === thread.team_id);
    return team ? team.name : t('INTERNAL_CHAT.THREAD.TEAM_CHANNEL');
  }
  return otherParticipant.value?.name || t('INTERNAL_CHAT.THREAD.DIRECT');
});

const headerSubtitle = computed(() =>
  activeThread.value?.thread_type === 'team'
    ? t('INTERNAL_CHAT.THREAD.TEAM_CHANNEL')
    : t('INTERNAL_CHAT.THREAD.DIRECT')
);

const isTeam = computed(() => activeThread.value?.thread_type === 'team');

const messages = computed(
  () => messagesByThread.value[activeThreadId.value] || []
);

const isOwn = message => message.sender?.id === currentUserId.value;

const messageTime = ts =>
  ts
    ? new Date(ts * 1000).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const scrollToBottom = async () => {
  await nextTick();
  const el = scrollContainer.value;
  if (el) el.scrollTop = el.scrollHeight;
};

const onScroll = async () => {
  const el = scrollContainer.value;
  if (!el || el.scrollTop > 40 || loadingOlder.value) return;
  const list = messages.value;
  if (!list.length) return;
  loadingOlder.value = true;
  const previousHeight = el.scrollHeight;
  try {
    await store.fetchMessages(activeThreadId.value, list[0].id);
    await nextTick();
    el.scrollTop = el.scrollHeight - previousHeight;
  } catch {
    error.value = true;
  } finally {
    loadingOlder.value = false;
  }
};

const onSend = async content => {
  error.value = false;
  try {
    await store.sendMessage(activeThreadId.value, content);
    await scrollToBottom();
  } catch {
    error.value = true;
  }
};

watch(
  () => messages.value.length,
  () => scrollToBottom()
);

watch(activeThreadId, () => {
  error.value = false;
  scrollToBottom();
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full min-w-0 bg-n-surface-1">
    <!-- Estado vazio -->
    <div
      v-if="!activeThreadId"
      class="flex flex-col flex-1 gap-3 justify-center items-center text-n-slate-10"
    >
      <span
        class="flex justify-center items-center rounded-2xl size-14 bg-n-alpha-1"
      >
        <span class="i-lucide-messages-square size-7" />
      </span>
      <p class="max-w-xs text-sm text-center">
        {{ t('INTERNAL_CHAT.WINDOW.NO_SELECTION') }}
      </p>
    </div>

    <template v-else>
      <!-- Header da conversa -->
      <header
        class="flex gap-3 items-center px-4 border-b h-14 border-n-weak shrink-0"
      >
        <span class="relative shrink-0">
          <span
            v-if="isTeam"
            class="flex justify-center items-center rounded-full size-9 bg-n-iris-3 text-n-iris-11"
          >
            <span class="i-lucide-users size-4.5" />
          </span>
          <Avatar
            v-else
            :name="headerTitle"
            :src="otherParticipant?.avatar_url"
            :size="36"
          />
          <AgentPresenceDot
            v-if="!isTeam && otherParticipant"
            :user-id="otherParticipant.id"
            class="absolute -right-0.5 -bottom-0.5 ring-2 ring-n-surface-1"
          />
        </span>
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-semibold truncate text-n-slate-12">
            {{ headerTitle }}
          </span>
          <span class="text-xs truncate text-n-slate-10">
            {{ headerSubtitle }}
          </span>
        </div>
      </header>

      <!-- Mensagens -->
      <div
        ref="scrollContainer"
        class="flex overflow-y-auto flex-col flex-1 gap-1 px-4 py-4"
        @scroll="onScroll"
      >
        <p
          v-if="loadingOlder"
          class="py-1 text-xs text-center text-n-slate-10"
        >
          {{ t('INTERNAL_CHAT.WINDOW.LOADING') }}
        </p>
        <p v-if="error" class="py-1 text-xs text-center text-n-ruby-11">
          {{ t('INTERNAL_CHAT.WINDOW.ERROR') }}
        </p>
        <p
          v-if="!messages.length && !loadingOlder"
          class="py-10 text-sm text-center text-n-slate-10"
        >
          {{ t('INTERNAL_CHAT.WINDOW.EMPTY') }}
        </p>

        <div
          v-for="message in messages"
          :key="message.id"
          class="flex gap-2 items-end max-w-[75%]"
          :class="isOwn(message) ? 'self-end flex-row-reverse' : 'self-start'"
        >
          <Avatar
            v-if="!isOwn(message)"
            :name="message.sender?.name || ''"
            :src="message.sender?.avatar_url"
            :size="28"
            class="shrink-0"
          />
          <div
            class="px-3.5 py-2 rounded-2xl shadow-sm"
            :class="
              isOwn(message)
                ? 'bg-n-brand text-white ltr:rounded-br-md rtl:rounded-bl-md'
                : 'bg-n-alpha-2 text-n-slate-12 ltr:rounded-bl-md rtl:rounded-br-md'
            "
          >
            <p
              v-if="!isOwn(message) && isTeam"
              class="mb-0.5 text-xs font-semibold text-n-iris-11"
            >
              {{ message.sender?.name }}
            </p>
            <p class="text-sm whitespace-pre-wrap break-words mb-0.5">
              {{ message.content }}
            </p>
            <p
              class="text-[10px] leading-none ltr:text-right rtl:text-left"
              :class="isOwn(message) ? 'text-white/70' : 'text-n-slate-10'"
            >
              {{ messageTime(message.created_at) }}
            </p>
          </div>
        </div>
      </div>

      <MessageComposer @send="onSend" />
    </template>
  </div>
</template>
